<lo-page-header
	title="Billing"
	subtitle="Manage your subscriptions and view usage."
>
	<button
		loActions
		lo-button
		variant="secondary"
		type="button"
		[loading]="portalOpening"
		(click)="openCustomerPortal()"
	>
		Customer Portal
	</button>
</lo-page-header>

<div class="content">
	@if (loading) {
	<ul class="cards" aria-hidden="true" aria-busy="true">
		@for (card of skeletonCards; track card) {
		<li>
			<lo-card>
				<lo-skeleton variant="text" width="55%"></lo-skeleton>
				<lo-skeleton variant="text"></lo-skeleton>
				@for (row of skeletonRows; track row) {
				<lo-skeleton variant="text"></lo-skeleton>
				}
				<lo-skeleton
					variant="block"
					height="calc(var(--lo-space-card) * 1.5)"
				></lo-skeleton>
			</lo-card>
		</li>
		}
	</ul>
	} @if (!loading && cards.length === 0) {
	<lo-empty-state
		heading="No active subscriptions"
		body="Choose a plan to get started."
	></lo-empty-state>
	} @if (!loading && cards.length > 0) {
	<ul class="cards" [class.single]="cards.length === 1">
		@for (card of cards; track trackByProduct($index, card)) {
		<li
			[attr.data-product-id]="card.productId"
			[attr.data-status]="card.subscription.subscription.status"
			[attr.data-phase]="card.subscription.phase"
		>
			<lo-card [heading]="card.productTitle" [headingLevel]="2">
				<lo-status
					loCardActions
					[tone]="statusTone(card)"
					[attr.data-status]="card.subscription.subscription.status"
				>
					{{ statusLabel(card) }}
				</lo-status>

				<div class="plan-row">
					<span class="lo-type-text-md-semibold">
						{{ card.subscription.plan.title }}
					</span>
					<span class="lo-type-text-md-semibold">
						${{ card.subscription.plan.price }} /
						{{ card.subscription.plan.interval }}
					</span>
				</div>

				@if (scheduledLabel(card); as scheduled) {
				<lo-notice
					tone="warning"
					[attr.data-change-type]="
						card.subscription.scheduled_change?.change_type
					"
					[heading]="scheduled"
				>
					<button
						lo-button
						variant="secondary"
						size="sm"
						type="button"
						[loading]="card.actionInFlight"
						(click)="uncancel(card)"
					>
						Undo
					</button>
				</lo-notice>
				} @if ( card.subscription.limits.length > 0 ||
				card.subscription.entitlements.length > 0 ) {
				<dl class="feature-rows lo-type-text-sm">
					@for ( ent of card.subscription.entitlements; track
					trackByLimit($index, ent) ) {
					<div class="row" [attr.data-alias]="ent.alias">
						<dt>{{ humanize(ent.alias) }}</dt>
						<dd>
							@if (entitlementValue(ent) === 'On') {
							<lo-status tone="success">On</lo-status>
							} @else if (entitlementValue(ent) === 'Off') {
							<lo-status tone="neutral">Off</lo-status>
							} @else {
							<span class="lo-type-text-sm-semibold">
								{{ entitlementValue(ent) }}
							</span>
							}
						</dd>
					</div>
					} @for ( limit of card.subscription.limits; track
					trackByLimit($index, limit) ) {
					<div class="row" [attr.data-alias]="limit.alias">
						<dt>{{ humanize(limit.alias) }}</dt>
						<dd class="lo-type-text-sm-semibold">
							{{ limit.used | number }} / @if (limit.cap !== null)
							{
							{{ limit.cap | number }}
							} @else { unlimited }
						</dd>
					</div>
					}
				</dl>
				} @else {
				<p class="lo-type-text-sm muted">
					No renewable limits on this plan.
				</p>
				}

				<div class="period">
					<span class="lo-type-text-sm muted">Period End</span>
					<span class="lo-type-text-sm-semibold">
						{{ card.subscription.period_end | date: 'mediumDate' }}
					</span>
				</div>

				<div loCardFooter class="card-actions">
					@if (!hasActiveScheduledChange(card) && !isAppTrial(card)) {
					<button
						lo-button
						variant="secondary"
						type="button"
						[disabled]="card.actionInFlight"
						(click)="cancel(card)"
					>
						Cancel
					</button>
					} @if (isAppTrial(card)) {
					<button
						lo-button
						type="button"
						[disabled]="card.actionInFlight"
						(click)="gotoPlans(card)"
					>
						Subscribe with Card
					</button>
					} @else {
					<button
						lo-button
						variant="secondary"
						type="button"
						[disabled]="card.actionInFlight"
						(click)="gotoPlans(card)"
					>
						Change Plan
					</button>
					}
				</div>
			</lo-card>
		</li>
		}
	</ul>
	} @if (!loading && hasSideUsage) {
	<lo-card
		heading="Outreach accounts"
		[headingLevel]="2"
		data-testid="side-usage"
	>
		<p class="lo-type-text-sm muted">
			How many sending accounts your plan allows, shared across all your
			subscriptions. The sends themselves are charged in credits.
		</p>

		<div class="side-stats">
			@for (limit of outreachLimits; track trackByLimit($index, limit)) {
			<div class="stat" [attr.data-alias]="limit.alias">
				<div class="stat-row lo-type-text-sm">
					<span class="muted">{{ humanize(limit.alias) }}</span>
					<span class="lo-type-text-sm-semibold">
						{{ limit.used | number }} / @if (limit.cap !== null) {
						{{ limit.cap | number }}
						} @else { unlimited }
					</span>
				</div>
				@if (limit.cap !== null) {
				<lo-progress
					[label]="humanize(limit.alias) + ' used'"
					[value]="limitPercent(limit.used, limit.cap)"
				></lo-progress>
				}
			</div>
			} @for (ent of outreachCaps; track trackByLimit($index, ent)) {
			<div class="stat" [attr.data-alias]="ent.alias">
				<div class="stat-row lo-type-text-sm">
					<span class="muted">{{ humanize(ent.alias) }}</span>
					<span class="lo-type-text-sm-semibold">
						{{ entitlementValue(ent) }}
					</span>
				</div>
				@if (ent.cap !== null && ent.used !== null) {
				<lo-progress
					[label]="humanize(ent.alias) + ' used'"
					[value]="limitPercent(ent.used, ent.cap)"
				></lo-progress>
				}
			</div>
			}
		</div>
	</lo-card>
	} @if (!loading) {
	<lo-card
		heading="API credits"
		[headingLevel]="2"
		data-testid="api-credits-section"
	>
		@if (apiCredits) {
		<button
			loCardActions
			lo-button
			size="sm"
			type="button"
			data-testid="add-credits"
			[disabled]="!creditPacks.length"
			(click)="openAddCredits()"
		>
			Add credits
		</button>
		}
		<p class="lo-type-text-sm muted">
			Calls to the Lolly public API are charged in credits from your Lolly
			balance.
		</p>

		<!-- Influencer API credit balance (accumulating pool, unused credits roll over). -->
		@if (apiCredits; as credits) {
		<lo-metric
			label="Credits remaining"
			data-testid="api-credits"
			[value]="(credits.balance_credits | number) ?? ''"
		></lo-metric>
		@if (apiCreditsGrant !== null) {
		<p class="lo-type-text-xs muted">
			Renews +{{ apiCreditsGrant | number }} credits / month · unused
			credits roll over
		</p>
		} }

		<!--
				Automatic top-up used to sit here, inside this block. It governs the
				PLATFORM balance — every metered action across Lolly draws on it, and the
				backend keyed its configuration by brand on 2026-09-09 — but this block
				only renders for a brand holding a Creator Data API subscription, because
				`GET /billing/credits` answers `has_subscription: false` to everyone
				else. So the one control that keeps the balance off zero was invisible to
				most brands. It now lives beside that balance, on the Credits page.
			-->

		<!-- Legacy Discovery allowance — only when there's no Influencer API sub. -->
		@if (!apiCredits) {
		<!-- Remaining API requests (the metered AI-analysis allowance). -->
		@if (apiUsage; as usage) {
		<div class="stat">
			<div class="stat-row lo-type-text-sm">
				<span class="muted">API requests</span>
				<span class="lo-type-text-sm-semibold">
					{{ usage.used | number }} / @if (usage.cap !== null) {
					{{ usage.cap | number }}
					} @else { unlimited }
				</span>
			</div>
			@if (usage.cap !== null) {
			<lo-progress
				label="API requests used"
				[value]="apiRequestsPercent"
			></lo-progress>
			} @if (apiRequestsRemaining !== null) {
			<p class="lo-type-text-xs muted">
				{{ apiRequestsRemaining | number }} requests remaining this
				period
			</p>
			}
		</div>
		}

		<!--
					There is no Discovery subscription to be included with any more: the
					unified catalogue replaced the per-product plans, and the public API
					is a product of its own — the Creator Data API — priced on its own
					ladder and never folded into the application tier.
				-->
		@if (!apiUsage) {
		<div class="api-quota-empty">
			<p class="lo-type-text-sm muted">
				The public API is its own product — the Creator Data API — with
				its own plan and rate. Talk to us about the volume you need and
				we will price it with you.
			</p>
			@if (apiDemoUrl) {
			<button
				lo-button
				variant="secondary"
				size="sm"
				type="button"
				data-testid="book-api-demo"
				(click)="bookApiDemo()"
			>
				Book a demo
			</button>
			}
		</div>
		} }
	</lo-card>
	}

	<!--
		API key — its own card, and it renders for two kinds of brand only.

		Minting a credential (`POST /api-keys`, `POST /api-keys/{id}/rotate`) is
		gated on an active Creator Data API subscription and answers 402 without one,
		so for a platform-only brand "Generate API key" was a button that could not
		work. Reading and revoking are deliberately left open by the same router, for
		the brand whose subscription lapsed while a live key is still out there — so
		the card stays for anyone who HAS a key, with the minting action disabled and
		saying why.
	-->
	@if (!loading && showApiKeyCard) {
	<lo-card heading="API access" [headingLevel]="2" data-testid="api-key">
		<p class="lo-type-text-sm muted">
			Look up influencers programmatically with the Lolly public API.
		</p>

		<!-- Full token, shown only once right after create / regenerate. -->
		@if (revealedToken; as token) {
		<lo-notice
			heading="Your API key"
			body="Copy it now — for security, this key won't be shown again."
			dismissible
			(dismissedChange)="dismissRevealedToken()"
		>
			<button lo-button size="sm" type="button" (click)="copyToken()">
				<lo-icon name="copy"></lo-icon>
				Copy
			</button>
		</lo-notice>
		<code class="token-value lo-type-text-md">{{ token }}</code>
		} @else {
		<!-- Existing key: identifier only (the secret is not retrievable). -->
		@if (activeApiKey; as key) {
		<div class="key-existing">
			<div class="key-id">
				<span class="lo-type-text-sm-medium muted">Active key</span>
				<code class="lo-type-text-md">{{ apiKeyMasked }}</code>
				<span class="lo-type-text-xs muted">
					Created {{ key.created_at | date: 'mediumDate' }} · @if
					(key.last_used_at) { Last used
					{{ key.last_used_at | date: 'mediumDate' }}
					} @else { Never used }
				</span>
				<!-- Rotation mints a credential, so it is gated exactly like
							     issuing one. Say so rather than sitting inert. -->
				@if (!hasApiSubscription) {
				<p
					class="lo-type-text-xs muted"
					data-testid="api-key-rotation-blocked"
				>
					Your Creator Data API plan is not active, so a new key
					cannot be issued. This one still works and you can still
					revoke it.
				</p>
				}
			</div>
			<button
				lo-button
				variant="secondary"
				type="button"
				[loading]="apiKeyActionInFlight"
				[disabled]="!hasApiSubscription"
				(click)="regenerateApiKey()"
			>
				Regenerate
			</button>
		</div>
		}

		<!-- No key yet. -->
		@if (!activeApiKey && !apiKeyLoading) {
		<div class="key-empty">
			<p class="lo-type-text-sm muted">
				No API key yet. Generate one to start using the API.
			</p>
			<button
				lo-button
				type="button"
				[loading]="apiKeyActionInFlight"
				(click)="generateApiKey()"
			>
				Generate API key
			</button>
		</div>
		} }
	</lo-card>
	}
</div>
